import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Badge } from 'antd';
import {
  DashboardOutlined,
  TransactionOutlined,
  CreditCardOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  SafetyOutlined,
  BankOutlined,
  WalletOutlined,
  PayCircleOutlined,
  LineChartOutlined,
  ApiOutlined,
  BookOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  MoneyCollectOutlined,
  QrcodeOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  SwapOutlined,
  SyncOutlined,
  SettingOutlined as RouteOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const Sidebar = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState(['transaction-management', 'user-management']);
  const [adminStats, setAdminStats] = useState({
    pendingApprovals: 0,
    pendingDeposits: 0
  });

  // Fetch admin statistics for badges
  useEffect(() => {
    const fetchAdminStats = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const response = await fetch('/api/dashboard/admin-stats', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Admin stats received:', data);
          setAdminStats({
            pendingApprovals: data.pendingApprovals || 0,
            pendingDeposits: data.pendingDeposits || 0
          });
          console.log('Admin stats state updated:', {
            pendingApprovals: data.pendingApprovals || 0,
            pendingDeposits: data.pendingDeposits || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
      }
    };

    fetchAdminStats();

    // Refresh stats every 30 seconds
    const interval = setInterval(fetchAdminStats, 30000);

    // Also refresh when window gains focus (user returns to tab)
    const handleFocus = () => {
      fetchAdminStats();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Helper function to create label with badge - always show badge for consistency
  const createLabelWithBadge = (text, count) => {
    return (
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        {text}
        <Badge
          count={count || 0}
          style={{ marginLeft: 8 }}
          showZero={false}
        />
      </span>
    );
  };

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'master-data',
      icon: <ShoppingOutlined />,
      label: 'Master Data',
      children: [
        {
          key: '/admin/master-products',
          icon: <ShoppingOutlined />,
          label: 'Master Produk',
        },
        {
          key: '/admin/fee-tiers',
          icon: <AppstoreOutlined />,
          label: 'Fee Tiers',
        },
        {
          key: '/admin/supplier-management',
          icon: <SettingOutlined />,
          label: 'Supplier Management',
        },
        {
          key: '/admin/category-management',
          icon: <AppstoreOutlined />,
          label: 'Category Management',
        },
        {
          key: '/admin/product-routing',
          icon: <RouteOutlined />,
          label: 'Product Routing',
        },
        {
          key: '/admin/deposit-banks',
          icon: <BankOutlined />,
          label: 'Deposit Banks',
        },
      ],
    },
    {
      key: 'transaction-management',
      icon: <TransactionOutlined />,
      label: 'Transaksi & Keuangan',
      children: [
        {
          key: '/admin/transactions',
          icon: <SwapOutlined />,
          label: 'Semua Transaksi',
        },
        // {
        //   key: '/admin/processing-transactions',
        //   icon: <SyncOutlined />,
        //   label: 'Processing (Manual)',
        // },
        {
          key: '/admin/cash-inout',
          icon: <WalletOutlined />,
          label: 'Cash In/Out',
        },
        {
          key: '/admin/merchant-balances',
          icon: <BankOutlined />,
          label: 'Saldo Merchant',
        },

        {
          key: '/admin/withdrawal-settings',
          icon: <SettingOutlined />,
          label: 'Pengaturan Withdrawal',
        },
        // {
        //   key: '/admin/analytics',
        //   icon: <LineChartOutlined />,
        //   label: 'Analytics',
        // },
      ],
    },
    // {
    //   key: 'payment-methods',
    //   icon: <CreditCardOutlined />,
    //   label: 'Metode Pembayaran',
    //   children: [
    //     {
    //       key: '/admin/qris-payment',
    //       icon: <QrcodeOutlined />,
    //       label: 'QRIS Payment',
    //     },
    //     {
    //       key: '/admin/qris-merchants',
    //       icon: <QrcodeOutlined />,
    //       label: 'QRIS Merchants',
    //     },
    //   ],
    // },
    {
      key: 'user-management',
      icon: <UserOutlined />,
      label: 'Manajemen Pengguna',
      children: [
        {
          key: '/admin/users',
          icon: <UserOutlined />,
          label: 'Pengguna Sistem',
        },
        // { key: '/admin/members', icon: <TeamOutlined />, label: 'Member' },
      ],
    },
    {
      key: 'validation-management',
      icon: <CheckCircleOutlined />,
      label: 'Validasi & Approval',
      children: [
        {
          key: '/admin/deposit-validation',
          icon: <CheckCircleOutlined />,
          label: createLabelWithBadge('Validasi Deposit', adminStats.pendingDeposits),
        },
        {
          key: '/admin/merchant-approval',
          icon: <AuditOutlined />,
          label: createLabelWithBadge('Merchant Approval', adminStats.pendingApprovals),
        },
        {
          key: '/admin/withdrawal-approval',
          icon: <BankOutlined />,
          label: 'Approval Withdrawal',
        },
        {
          key: '/admin/merchant-bank-accounts',
          icon: <BankOutlined />,
          label: 'Verifikasi Rekening',
        },
        {
          key: '/admin/supplier-reconciliation',
          icon: <SyncOutlined />,
          label: 'Rekonsiliasi Supplier',
        },
      ],
    },
    {
      key: 'system-management',
      icon: <SettingOutlined />,
      label: 'Sistem & Konfigurasi',
      children: [
        // {
        //   key: '/admin/config',
        //   icon: <ApiOutlined />,
        //   label: 'Konfigurasi',
        // },
        // admin/security disembunyikan: IP whitelist diganti per-merchant, signature tool sudah obsolete (RSA)
        // {
        //   key: '/admin/security',
        //   icon: <SafetyOutlined />,
        //   label: 'Security',
        // },
        {
          key: '/admin/callback-config',
          icon: <ApiOutlined />,
          label: 'Callback Config',
        },
        {
          key: '/admin/settings',
          icon: <SettingOutlined />,
          label: 'Pengaturan',
        },
        // {
        //   key: '/admin/tps-testing',
        //   icon: <ThunderboltOutlined />,
        //   label: 'TPS Testing',
        // },
      ],
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Laporan & Dokumentasi',
      children: [
        {
          key: '/admin/reports',
          icon: <FileTextOutlined />,
          label: 'Laporan',
        },
        // API Documentation menu - Hidden for now
        // {
        //   key: '/api-documentation',
        //   icon: <BookOutlined />,
        //   label: 'API Documentation',
        // },
      ],
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Pengaturan',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Keluar',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key, keyPath }) => {
    // Handle API Documentation specially - open in new tab
    // if (key === '/api-documentation') {
    //   window.open('/api-documentation', '_blank');
    //   return;
    // }
    // If it's a parent category (doesn't start with '/'), don't navigate
    if (!key.startsWith('/')) {
      return;
    }
    navigate(key);
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  return (
    <>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ height: '100%', borderRight: 0 }}
      />
    </>
  );
};

export default Sidebar;